//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  IGetInstagramShopsItemsByCuidResponse,
  IGetShopTagResponse,
  IGetShopTagsRequest,
  IGetShopTagsResponse,
  IInstagramShopsAutocompleteRequest,
  IInstagramShopsCatalog,
  IInstagramShopsCatalogResponse,
  IInstagramShopsCatalogSearchRequest,
  IInstagramShopsCatalogSearchResponse,
  IInstagramShopsEligibilityResponse,
  IInstagramShopsFeatureRequest,
  IInstagramShopsItemsRequest,
  IInstagramShopsItemsSearchRequest,
  IInstagramShopsItemsSearchResponse,
  IInstagramShopsProduct,
  IInstagramShopsSearchResult,
} from '@apis/instagram-shops/instagramShopsApiModel';
import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import { IInstagramAutocompleteResponse } from '@instagram-account/models/IInstagramAutocompleteResponses';
import { IInstagramSearchRequestBody } from '@instagram-account/models/IInstagramSearchRequestBody';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const INSTAGRAM_USER_ENDPOINT = '/api/v2/instagram/user';
const INSTAGRAM_USER_DEFAULT_ENDPOINT = `${INSTAGRAM_USER_ENDPOINT}/default`;

function instagramShopsFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/instagram-shops${endpoint}`,
    init,
  );
}

const instagramUserFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `${INSTAGRAM_USER_ENDPOINT}${endpoint}`,
    init,
  );
};

const instagramDefaultUserFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `${INSTAGRAM_USER_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 *
 * Gets client eligibility for IG Shop Tags
 *
 * GET /api/v2/instagram/user/default/shop-tag-eligibility
 */
export async function getInstagramShopTagEligibility(): Promise<boolean> {
  const res = await instagramDefaultUserFetch(`/shop-tag-eligibility`);
  const eligibilityResponse =
    await res.json<IInstagramShopsEligibilityResponse>();

  return eligibilityResponse.isShopTagsEligible;
}

/**
 *
 * Gets IG Shops catalogs for the client
 *
 * GET /api/v2/instagram/user/catalog
 */
export async function getInstagramShopTagCatalogs(): Promise<
  IInstagramShopsCatalog[]
> {
  const res = await instagramUserFetch(`/catalog`);
  const catalogsResponse = await res.json<IInstagramShopsCatalogResponse>();

  return catalogsResponse.catalogs;
}

/**
 *
 * Searches the provided IG SHops catalog for matching products
 *
 * GET /api/v2/instagram/user/catalog/search
 */
export async function searchInstagramShopTagCatalog(
  req: IInstagramShopsCatalogSearchRequest,
  abortSignal?: AbortSignal,
): Promise<IInstagramShopsProduct[]> {
  const params = makeParams(req);
  const res = await instagramUserFetch(
    `/catalog/search?${params}`,
    isDefined(abortSignal) ? { signal: abortSignal } : undefined,
  );
  const searchResponse = await res.json<IInstagramShopsCatalogSearchResponse>();

  return searchResponse.products;
}

/**
 *
 * Searches for IG Shops items for the current client
 *
 * POST /api/v1/instagram-shops/items
 */
export async function getInstagramShopsItems(
  req: IInstagramShopsItemsSearchRequest,
  body?: IInstagramSearchRequestBody,
): Promise<IInstagramShopsSearchResult> {
  const params = makeParams(req);
  const res = await instagramShopsFetch(`/items?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  const instagramShopsItemsResponse =
    await res.json<IInstagramShopsItemsSearchResponse>((res) => ({
      data: {
        items: res.data.items.map(deserializeMediaItem),
        totalCount: res.data.totalCount,
      },
    }));

  return instagramShopsItemsResponse.data;
}

/**
 *
 * Gets autocomplete suggestions for Instagram Shops items
 *
 * POST /api/v1/instagram-shops/autocomplete
 */
export async function getInstagramShopsAutocompleteSuggestions(
  req: IInstagramShopsAutocompleteRequest,
  body?: IInstagramSearchRequestBody,
): Promise<IInstagramAutocompleteResponse> {
  const params = makeParams(req);
  const res = await instagramShopsFetch(`/autocomplete?${params}`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
  return res.json();
}

/**
 *
 * Initiates a request to feature the given media on the IG Shop pages of the provided products
 *
 * POST /api/v1/instagram-shops/featured
 */
export async function requestFeatureOnInstagramShops(
  body: IInstagramShopsFeatureRequest,
): Promise<void> {
  await instagramShopsFetch(`/featured`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 *
 * Gets the materialized IG Shops items by cuid
 *
 * POST /api/v1/instagram-shops/items/materialized
 */
export async function getInstagramShopsItemsByCuid(
  body: IInstagramShopsItemsRequest,
): Promise<IMlsMediaItem[]> {
  const res = await instagramShopsFetch(`/items/materialized`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  const itemsResponse = await res.json<IGetInstagramShopsItemsByCuidResponse>(
    (res) => ({
      data: res.data.map(deserializeMediaItem),
    }),
  );

  return itemsResponse.data;
}

/**
 * Gets a batch of shopTags by ids
 *
 * /api/v1/instagram-shops/shop-tags
 */
export const getShopTags = async (
  catalogId: string,
  body: IGetShopTagsRequest,
): Promise<IGetShopTagsResponse> => {
  const res = await instagramShopsFetch(`/shop-tags?catalogId=${catalogId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return await res.json<IGetShopTagsResponse>();
};
